import { Component, Input, OnInit, Optional, TemplateRef, ViewChild } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { CellContext, TableColumn } from '@swimlane/ngx-datatable';
import { ClientFiltrableDataSource } from 'filtrable-data-source';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, finalize, take, tap } from 'rxjs/operators';
import { DeviceParametersDtoNBK, DeviceParamNBK, DeviceParamsService, DeviceService } from 'src/app/api/nbk';
import { AuthService } from 'src/app/core/auth.service';
import { canServiceUserEdit } from 'src/app/core/permission.utils';
import { getFileNameFromHttpHeader, getTimeZone, KEY_LANGUAGE_STORAGE } from 'src/app/core/utils';
import { DatePipe } from '@angular/common';
import { DatatableComponent } from '../../datatable/datatable/datatable.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FromUTCToLocalePipe } from '../../pipes/from-utcto-locale.pipe';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpResponse } from '@angular/common/http';

@Component({
    selector: 'app-parameter-device',
    templateUrl: './parameter-device.component.html',
    styleUrls: ['./parameter-device.component.scss'],
    imports: [DatatableComponent, ReactiveFormsModule, FormsModule, DatePipe, 
      TranslatePipe, FromUTCToLocalePipe
    ]
})
export class ParameterDeviceComponent implements OnInit {
  @Input() deviceId: string;
  
  deviceParams: DeviceParametersDtoNBK;
  dataSource = new ClientFiltrableDataSource<DeviceParamNBK>();
  columns: TableColumn[] = [];

  lang: string;
  timezone: string;
  isSavingParams: boolean = false;
  isLoading: boolean = true;

  @ViewChild('valueTpl', { static: true }) valueTpl: TemplateRef<CellContext<any>>;

  constructor(
    @Optional() public activeModal: NgbActiveModal,
    private ts: TranslateService,
    private toastrService: ToastrService,
    private deviceService: DeviceService,
    private deviceParamsService: DeviceParamsService,
    public authService: AuthService
  ) {
    this.dataSource.limit = 8;
  }

  ngOnInit(): void {
    this.lang = localStorage.getItem(KEY_LANGUAGE_STORAGE)!;
    this.timezone = getTimeZone();
    
    this.columns = [
      {
        prop: 'id',
        name: this.ts.instant('DEVICES.DEVICE_CARD.MODALS.PARAMETER.COLUMN.ID'),
        maxWidth: 100
      },
      {
        prop: 'name',
        name: this.ts.instant('DEVICES.DEVICE_CARD.MODALS.PARAMETER.COLUMN.NAME'),
      },
      {
        prop: 'value',
        name: this.ts.instant('DEVICES.DEVICE_CARD.MODALS.PARAMETER.COLUMN.VALUE'),
        cellTemplate: this.valueTpl,
      },
      {
        prop: 'min',
        name: this.ts.instant('DEVICES.DEVICE_CARD.MODALS.PARAMETER.COLUMN.MIN'),
        maxWidth: this.activeModal ? 150 : undefined
      },
      {
        prop: 'max',
        name: this.ts.instant('DEVICES.DEVICE_CARD.MODALS.PARAMETER.COLUMN.MAX'),
        maxWidth: this.activeModal ? 150 : undefined
      },
      {
        prop: 'defValue',
        name: this.ts.instant('DEVICES.DEVICE_CARD.MODALS.PARAMETER.COLUMN.DEFAULT'),
        maxWidth: this.activeModal ? 150 : undefined
      },
    ];

    this.getParams();
  }

  getParams() {
    this.isLoading = true;
    this.deviceParamsService
      .getDeviceParams(+this.deviceId!)
      .pipe(
        tap((res) => {
          this.deviceParams = res;
          this.dataSource.setItems(res.parameters || []);
        }),
        catchError((error) => {
          this.deviceParams = {
            deviceId: +this.deviceId
          };
          return of(null);
        }),
        finalize(() => this.isLoading = false)
      )
      .subscribe();
  }

  saveParams() {
    this.dataSource.items?.forEach( (elem, index) => {
      const param = this.deviceParams.parameters![index];
      param.value = elem.value;
    })

    this.isSavingParams = true;

    this.deviceParamsService
      .setDeviceParams(+this.deviceId, this.deviceParams)
      .pipe(
        tap(() => {
          this.toastrService.success(
            this.ts.instant('DEVICES.DEVICE_CARD.MODALS.SAVE_PARAMS'),
            this.ts.instant('GLOBAL.SUCCESS')
          );
        }),
        finalize(() => (this.isSavingParams = false))
      )
      .subscribe();
  }

  downloadParams() {
    this.deviceParamsService.downloadDeviceParams(+this.deviceId!, "response")
      .pipe(take(1))
      .subscribe((data: HttpResponse<any>) => {
        const fileName = getFileNameFromHttpHeader(data);
        const url = window.URL.createObjectURL(data.body);
        var fileLink = document.createElement('a');
        fileLink.href = url;
        fileLink.download = fileName;
        fileLink.click();
    });
  }

  canEdit() {
    return canServiceUserEdit(this.authService);
  }

}
